import React, { useState, useEffect, useCallback, useMemo } from 'react';
import useUrlState from '../../hooks/useUrlState';
import { useToast } from '../../components/Toast';
import Header from '../../components/Header';
import KpiCard from '../../components/KpiCard';
import { styles, colors, radius, shadows } from '../../styles/theme';
import { formatSayi } from '../../utils/analytics/format';
import { exportCsv } from '../../utils/analytics/export';
import { logError } from '../../utils/logError';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8001';

const getAuthHeaders = () => {
  const token = localStorage.getItem('access_token');
  return token ? { 'Authorization': `Bearer ${token}` } : {};
};

// Sınıflandırma rozetleri (tema renkleriyle)
const SINIF_ROZET = {
  KAYNAK_YOK: { label: 'Kaynak Yok', bg: colors.dangerLight, fg: '#991b1b' },
  SATINALMA: { label: 'Satınalma', bg: colors.warningLight, fg: '#92400e' },
  URETIM: { label: 'Üretim', bg: colors.successLight, fg: '#166534' },
  TRANSFER: { label: 'Transfer', bg: colors.infoLight, fg: '#075985' },
};

const BAYRAK_LABEL = {
  GIRIS_YOK: 'Hiç giriş yok',
  HIC_ALIS_YOK: 'Hiç alış yok',
  ALIS_EKSIK: 'Alış eksik',
  SATIS_ALISTAN_ONCE: 'Satış alıştan önce',
  URETIM_EKSIK: 'Üretim eksik',
  TRANSFER_EKSIK: 'Transfer eksik',
};

const SINIF_SECENEK = [
  { value: 'KAYNAK_YOK', label: 'Kaynak Yok' },
  { value: 'SATINALMA', label: 'Satınalma' },
  { value: 'URETIM', label: 'Üretim' },
  { value: 'TRANSFER', label: 'Transfer' },
];

const fmtTarih = (s) => (s ? new Date(s).toLocaleDateString('tr-TR') : '-');

const AlissizTuketilenPage = () => {
  const toast = useToast();

  const [data, setData] = useState(null);
  const [lokasyonlar, setLokasyonlar] = useState([]);
  const [loading, setLoading] = useState(true);

  const [lokasyonId, setLokasyonId] = useUrlState('lokasyon', '');
  const [sinif, setSinif] = useUrlState('sinif', '');
  const [arama, setArama] = useUrlState('arama', '', { debounceMs: 300 });

  // Lokasyon listesi (komşu SayimKaybiRaporuPage deseni — düz dizi)
  useEffect(() => {
    fetch(`${API_URL}/api/lokasyonlar`, { headers: getAuthHeaders() })
      .then(r => r.ok ? r.json() : [])
      .then(d => setLokasyonlar(Array.isArray(d) ? d : []))
      .catch(err => logError('Lokasyonlar yüklenemedi:', err));
  }, []);

  const fetchRapor = useCallback(async () => {
    setLoading(true);
    try {
      let url = `${API_URL}/api/stok/alissiz-tuketilen`;
      if (lokasyonId) url += `?lokasyon_id=${lokasyonId}`;

      const res = await fetch(url, { headers: getAuthHeaders() });
      if (res.ok) {
        setData(await res.json());
      } else {
        const e = await res.json().catch(() => ({}));
        toast.error(e.detail || 'Rapor yüklenemedi');
      }
    } catch (err) {
      logError('Alışsız tüketilen raporu yüklenemedi:', err);
      toast.error('Sunucu hatası — bağlantıyı kontrol edin');
    } finally {
      setLoading(false);
    }
  }, [lokasyonId, toast]);

  useEffect(() => {
    fetchRapor();
  }, [fetchRapor]);

  // Sınıf + arama client-side; lokasyon API'de filtrelenir
  const kayitlar = useMemo(() => {
    let list = data?.kayitlar || [];
    if (sinif) list = list.filter(k => k.siniflandirma === sinif);
    if (arama) {
      const q = arama.toLocaleLowerCase('tr');
      list = list.filter(k =>
        (k.malzeme_ad || '').toLocaleLowerCase('tr').includes(q) ||
        (k.malzeme_kod || '').toLocaleLowerCase('tr').includes(q)
      );
    }
    return list;
  }, [data, sinif, arama]);

  const exportCSV = () => {
    if (!kayitlar.length) return;
    exportCsv(kayitlar.map(k => ({
      Malzeme: k.malzeme_ad || '',
      Kod: k.malzeme_kod || '',
      Birim: k.birim || '',
      Lokasyon: k.lokasyon_ad || '',
      'Sınıf': SINIF_ROZET[k.siniflandirma]?.label || k.siniflandirma || '',
      Bayraklar: (k.bayraklar || []).map(b => BAYRAK_LABEL[b] || b).join(' | '),
      'Alış': k.toplam_alis,
      'Üretim Giriş': k.toplam_uretim_giris,
      'Transfer Giriş': k.toplam_transfer_giris,
      'Diğer Giriş': k.toplam_diger_giris,
      'Tüketim': k.toplam_tuketim,
      'Açık': k.acik,
      'Açık %': k.acik_oran != null ? (k.acik_oran * 100).toFixed(1) : '',
      'İlk Satış': k.ilk_satis_tarihi ? fmtTarih(k.ilk_satis_tarihi) : '',
      'İlk Alış': k.ilk_alis_tarihi ? fmtTarih(k.ilk_alis_tarihi) : '',
      'Güncel Stok': k.guncel_stok,
    })), 'alissiz-tuketilen.csv');
  };

  const ozet = data?.ozet;

  return (
    <div>
      <Header title="Alışsız Tüketilen Kalemler" />
      <div style={{ padding: '20px', background: colors.background, minHeight: 'calc(100vh - 60px)' }}>

        {/* Filtre Bar */}
        <div style={{ background: colors.surface, borderRadius: radius.lg, padding: '16px 20px', boxShadow: shadows.md, marginBottom: '12px' }}>
          <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'center' }}>
            <select value={lokasyonId} onChange={e => setLokasyonId(e.target.value)}
              style={{ padding: '5px 8px', border: `1px solid ${colors.border}`, borderRadius: radius.sm, fontSize: '12px', background: colors.surface }}>
              <option value="">Tüm Lokasyonlar</option>
              {lokasyonlar.map(l => <option key={l.id} value={l.id}>{l.ad}</option>)}
            </select>

            <select value={sinif} onChange={e => setSinif(e.target.value)}
              style={{ padding: '5px 8px', border: `1px solid ${colors.border}`, borderRadius: radius.sm, fontSize: '12px', background: colors.surface }}>
              <option value="">Tüm Sınıflar</option>
              {SINIF_SECENEK.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
            </select>

            <input
              value={arama}
              onChange={e => setArama(e.target.value)}
              placeholder="Malzeme ara…"
              style={{ padding: '5px 8px', border: `1px solid ${colors.border}`, borderRadius: radius.sm, fontSize: '12px', minWidth: '200px' }}
            />
          </div>
        </div>

        {/* Bilgi notu */}
        <div style={{ fontSize: '12px', color: colors.textTertiary, marginBottom: '20px', lineHeight: '1.5' }}>
          Tüketim (POS satışı) var ama alış girişi (fatura/fiyat girişi/GRN) yok ya da eksik olan (malzeme × lokasyon) kalemler.
          Üretilen ve transfer kalemler kendi giriş kanalıyla elenir; eşik üstündeki açık değerleri salt-okunur listelenir.
        </div>

        {loading && !data ? (
          <div style={{ textAlign: 'center', padding: '60px', color: colors.textTertiary }}>Yükleniyor…</div>
        ) : !data ? (
          <div style={{ textAlign: 'center', padding: '60px', color: colors.textTertiary }}>Veri bulunamadı</div>
        ) : (
          <>
            {/* KPI Cards */}
            {ozet && (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px', marginBottom: '20px' }}>
                <KpiCard title="Kritik" value={formatSayi(ozet.kritik || 0, { decimals: 0 })} color={styles.danger} icon="🚨" />
                <KpiCard title="Uyarı" value={formatSayi(ozet.uyari || 0, { decimals: 0 })} color={styles.warning} icon="⚠️" />
                <KpiCard title="Toplam Tahmini Açık" value={formatSayi(ozet.toplam_tahmini_acik || 0, { decimals: 2 })} color={styles.info} icon="📉" />
                <KpiCard title="Eşik" value={`%${((ozet.esik_oran || 0) * 100).toFixed(1)}`} color={styles.secondary} icon="🎯" />
              </div>
            )}

            {/* Tablo */}
            <div style={{ background: colors.surface, borderRadius: radius.lg, padding: '20px', boxShadow: shadows.md }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                <h3 style={{ margin: 0, fontSize: '15px', color: '#333' }}>Kalemler ({kayitlar.length})</h3>
                <button onClick={exportCSV} disabled={!kayitlar.length} style={{
                  padding: '6px 14px', border: `1px solid ${styles.primary}`, borderRadius: radius.sm,
                  background: colors.surface, color: styles.primary, fontSize: '12px', fontWeight: '600',
                  cursor: kayitlar.length ? 'pointer' : 'not-allowed', opacity: kayitlar.length ? 1 : 0.5
                }}>CSV İndir</button>
              </div>

              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                  <thead>
                    <tr style={{ background: colors.backgroundAlt, borderBottom: `2px solid ${colors.border}` }}>
                      <th style={th}>Malzeme</th>
                      <th style={th}>Lokasyon</th>
                      <th style={th}>Sınıf</th>
                      <th style={th}>Bayraklar</th>
                      <th style={thR}>Alış</th>
                      <th style={thR}>Tüketim</th>
                      <th style={thR}>Açık</th>
                      <th style={th}>İlk Satış</th>
                      <th style={th}>İlk Alış</th>
                      <th style={thR}>Güncel Stok</th>
                    </tr>
                  </thead>
                  <tbody>
                    {kayitlar.map(k => {
                      const rozet = SINIF_ROZET[k.siniflandirma] || { label: k.siniflandirma, bg: '#f1f5f9', fg: '#475569' };
                      const neg = k.guncel_stok != null && k.guncel_stok < 0;
                      return (
                        <tr key={`${k.malzeme_id}-${k.lokasyon_id}`} style={{ borderBottom: '1px solid #f0f0f0' }}>
                          <td style={td}>
                            <div style={{ fontWeight: '600' }}>{k.malzeme_ad}</div>
                            <div style={{ color: '#888', fontSize: '11px' }}>{k.malzeme_kod} · {k.birim}</div>
                          </td>
                          <td style={td}>{k.lokasyon_ad || '-'}</td>
                          <td style={td}>
                            <span style={{
                              background: rozet.bg, color: rozet.fg, padding: '2px 8px',
                              borderRadius: radius.full, fontSize: '11px', fontWeight: '600', whiteSpace: 'nowrap'
                            }}>{rozet.label}</span>
                          </td>
                          <td style={td}>
                            {(k.bayraklar || []).map(b => (
                              <span key={b} style={{
                                display: 'inline-block', background: '#f1f5f9', color: '#475569',
                                padding: '1px 6px', borderRadius: radius.sm, fontSize: '10px',
                                marginRight: '4px', marginBottom: '2px'
                              }}>{BAYRAK_LABEL[b] || b}</span>
                            ))}
                          </td>
                          <td style={tdR}>{formatSayi(k.toplam_alis, { decimals: 3, minDecimals: 0 })}</td>
                          <td style={tdR}>{formatSayi(k.toplam_tuketim, { decimals: 3, minDecimals: 0 })}</td>
                          <td style={{ ...tdR, fontWeight: '600', color: styles.danger }}>
                            {formatSayi(k.acik, { decimals: 3, minDecimals: 0 })}
                          </td>
                          <td style={td}>{fmtTarih(k.ilk_satis_tarihi)}</td>
                          <td style={td}>{fmtTarih(k.ilk_alis_tarihi)}</td>
                          <td style={{ ...tdR, color: neg ? styles.danger : '#333', fontWeight: neg ? '700' : '400' }}>
                            {formatSayi(k.guncel_stok, { decimals: 3, minDecimals: 0 })}
                          </td>
                        </tr>
                      );
                    })}
                    {!kayitlar.length && (
                      <tr>
                        <td colSpan={10} style={{ padding: '24px', textAlign: 'center', color: colors.textTertiary }}>
                          Kayıt bulunamadı — eşiğin üstünde alışsız tüketilen kalem yok.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

const th = { padding: '10px 8px', textAlign: 'left', fontWeight: '600', color: '#555', whiteSpace: 'nowrap' };
const thR = { ...th, textAlign: 'right' };
const td = { padding: '8px', verticalAlign: 'top' };
const tdR = { ...td, textAlign: 'right', whiteSpace: 'nowrap' };

export default AlissizTuketilenPage;
